// pages/social-login/gis.ts — Google Identity Services (GIS) popup code client.
//
// We use the OAuth "authorization code" model in POPUP mode (`initCodeClient`), keeping our own custom
// nova button as the trigger. GIS returns an authorization `code` to the JS callback; the backend
// exchanges it (verified in sso.ts: `exchangeGoogleCode` forwards the FE `redirect_uri` verbatim). For
// the popup model the exchange `redirect_uri` is the special value `postmessage` (below), so no real
// redirect URI needs registering in the Google console, only authorized JS origins. PKCE is not used on
// this path (confidential client: the secret lives on the backend), so no `code_verifier` is sent.

/** The redirect_uri the backend must use to exchange a popup-code (GIS postMessage model). */
export const GOOGLE_POPUP_REDIRECT_URI = 'postmessage';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

interface CodeClientResponse {
  code?: string;
  error?: string;
  error_description?: string;
}
interface CodeClient {
  requestCode: () => void;
}
interface GoogleOAuth2 {
  initCodeClient: (config: {
    client_id: string;
    scope: string;
    ux_mode: 'popup';
    // A space-delimited list of OAuth prompts. GIS defaults this to 'consent select_account', which
    // FORCES the consent screen on every sign-in even for a returning user who already granted access.
    // We pass '' (empty) so Google prompts only when it actually must: consent only on the first grant,
    // the account chooser only when several Google sessions exist. Never 'none' here: that is the silent
    // model and errors the moment any interaction (first consent, account pick) is needed.
    prompt?: string;
    callback: (response: CodeClientResponse) => void;
    error_callback?: (error: { type?: string }) => void;
  }) => CodeClient;
}
interface GoogleGlobal {
  accounts?: { oauth2?: GoogleOAuth2 };
}

function googleOAuth2(): GoogleOAuth2 | undefined {
  return (window as unknown as { google?: GoogleGlobal }).google?.accounts?.oauth2;
}

let loader: Promise<GoogleOAuth2> | null = null;

/** Load the GIS script once and resolve its `oauth2` namespace. */
export function loadGis(): Promise<GoogleOAuth2> {
  const ready = googleOAuth2();
  if (ready) {
    return Promise.resolve(ready);
  }
  if (loader) {
    return loader;
  }
  loader = new Promise<GoogleOAuth2>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    const onLoad = (): void => {
      const oauth2 = googleOAuth2();
      if (oauth2) {
        resolve(oauth2);
      } else {
        reject(new Error('GIS loaded but google.accounts.oauth2 is unavailable'));
      }
    };
    if (existing) {
      existing.addEventListener('load', onLoad, { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load GIS')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener(
      'error',
      () => {
        // Remove the failed tag so the next attempt appends a fresh one. Left in the DOM, this errored
        // tag would be picked up by the `existing` query above on the retry, which then attaches
        // load/error listeners to events that already fired and never replay — hanging the retry forever.
        script.remove();
        reject(new Error('Failed to load GIS'));
      },
      { once: true },
    );
    document.head.appendChild(script);
  }).catch((error) => {
    // Let a later attempt retry rather than caching the rejection forever.
    loader = null;
    throw error;
  });
  return loader;
}

/** Open the Google popup and resolve the authorization `code` (rejects on error / user-close). */
export async function requestGoogleCode(clientId: string): Promise<string> {
  const oauth2 = await loadGis();
  return new Promise<string>((resolve, reject) => {
    const client = oauth2.initCodeClient({
      client_id: clientId,
      scope: 'openid email profile',
      ux_mode: 'popup',
      // Skip the forced re-consent for a returning user (see the interface comment on `prompt`).
      prompt: '',
      callback: (response) => {
        if (response.code) {
          resolve(response.code);
        } else {
          reject(new Error(response.error || 'Google sign-in was cancelled'));
        }
      },
      error_callback: (error) => reject(new Error(error.type || 'Google sign-in failed')),
    });
    client.requestCode();
  });
}
